/**
 * THE WELCOME PAGE'S ADDRESS (design 25, section 6), in a file of its own so that what links here
 * - the session gate's detour (src/router.tsx), the Overview's "Getting started" row - imports an
 * address and not the page. Ours to name, like every address but `/enter` (decision-log row 99).
 *
 * WHO LANDS HERE. A reader signed in with NO author row, from any cabinet screen: the cabinet's
 * pages are for an author row, an applicant's included, and "Become an author" is the one way a
 * row is made on purpose (design 25, P16). And an applicant, from "Getting started", for the two
 * steps after it: their author profile, and their Payment details.
 */
export const WELCOME_PATH = '/welcome';

/**
 * WHERE THE TWO STEPS ARE, KEPT IN THE ADDRESS (design 25, section 6). Each step's "Continue"
 * leaves for its page - the author profile, Payment details - so the step it comes back to cannot
 * live in the page's memory: before leaving, the page REPLACES its own address with the next step,
 * and Back from the step's page lands there, not on the first step again. "Skip for now" moves the
 * same way and remembers that something waits, for the last step's line.
 */
export type WelcomeStep = 'profile' | 'payment' | 'done';

export interface WelcomePlace {
  readonly step: WelcomeStep;
  /** True once a step was skipped: the end then says where it waits. */
  readonly skipped: boolean;
}

const STEP_PARAM = 'step';
const SKIPPED_PARAM = 'skipped';

/** The place a `?step=...&skipped=1` query names; the first step for anything else. */
export function welcomePlaceFrom(search: string): WelcomePlace {
  const query = new URLSearchParams(search);
  const named = query.get(STEP_PARAM);
  const step: WelcomeStep = named === 'payment' || named === 'done' ? named : 'profile';
  return { step, skipped: query.get(SKIPPED_PARAM) === '1' };
}

/** The address of a place: `/welcome` for the first step, else with its query. */
export function welcomeAddressOf({ step, skipped }: WelcomePlace): string {
  const query = new URLSearchParams();
  if (step !== 'profile') {
    query.set(STEP_PARAM, step);
  }
  if (skipped) {
    query.set(SKIPPED_PARAM, '1');
  }
  const text = query.toString();
  return text === '' ? WELCOME_PATH : `${WELCOME_PATH}?${text}`;
}
